/**
 * ControlledSubstanceRegistryPage — searchable audit log of all controlled-substance
 * approvals and their linked sales. Required by regulatory compliance.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, ShieldCheck, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import api from '../lib/api';
import { PendingControlledRequests } from '../components/controlled/PendingControlledRequests';
import { RegisterExport } from '../components/controlled/RegisterExport';
import { usePermission } from '../hooks/usePermission';

const STATUS_CLS = {
  approved: 'bg-emerald-100 text-emerald-700',
  consumed: 'bg-sky-100 text-sky-700',
};

export default function ControlledSubstanceRegistryPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { allowed: canApprove } = usePermission('rx_approve');
  const debounceRef = useRef(null);
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip: page * limit, limit };
    if (search.trim()) params.q = search.trim();
    api.get('/sales/controlled-registry', { params })
      .then((r) => {
        setRows(r.data.approvals || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Failed to load registry'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (value) => {
    setSearch(value);
    setPage(0);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {}, 0); // state change triggers load via useEffect
  };

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 max-w-5xl" data-testid="controlled-registry-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-amber-600" />
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Controlled Substance Registry
        </h1>
        <span className="text-xs text-muted-foreground">({total} records)</span>
      </div>
      <p className="text-sm text-muted-foreground">
        Audit trail of all controlled substance approvals, linked sales, and compliance records.
      </p>
      <PendingControlledRequests />
      {canApprove && <RegisterExport />}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search by patient, prescriber, approval ID..."
          className="pl-9 h-9 text-sm"
          data-testid="registry-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No controlled substance approvals recorded yet.
        </div>
      ) : (
        <>
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs">
                <tr>
                  <th className="p-3 text-left font-medium">Approval ID</th>
                  <th className="p-3 text-left font-medium">Patient</th>
                  <th className="p-3 text-left font-medium">Prescriber</th>
                  <th className="p-3 text-left font-medium">Rx #</th>
                  <th className="p-3 text-left font-medium">Approved by</th>
                  <th className="p-3 text-left font-medium">Date</th>
                  <th className="p-3 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.approval_id} className="border-t" data-testid={`registry-row-${r.approval_id}`}>
                    <td className="p-3 font-mono text-xs">{r.approval_id}</td>
                    <td className="p-3">{r.patient_name || '—'}</td>
                    <td className="p-3">{r.prescriber_name || '—'}</td>
                    <td className="p-3">{r.prescription_number || '—'}</td>
                    <td className="p-3">{r.approved_by_name || r.approved_by || '—'}</td>
                    <td className="p-3 text-xs whitespace-nowrap">
                      {r.approved_at ? new Date(r.approved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                    </td>
                    <td className="p-3">
                      <Badge className={`text-[10px] ${STATUS_CLS[r.status] || 'bg-muted'}`}>{r.status}</Badge>
                      {r.sale_id && <span className="ml-1 text-[10px] text-muted-foreground">→ {r.sale_id}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="registry-prev">
                <ChevronLeft className="h-3 w-3 mr-1" /> Previous
              </Button>
              <span className="text-xs text-muted-foreground">Page {page + 1} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} data-testid="registry-next">
                Next <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
